import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';

type ConsumerType = 'Open Access' | 'Captive';
type ConsumerStatus = 'Active' | 'Inactive';

type ConsumerUtility = {
  utility_id: number;
  code: string;
  name: string;
  category: string;
  state: string;
};

type ConsumerProfile = {
  consumer_id: number;
  name: string;
  code: string;
  consumer_type: ConsumerType;
  utility_id: number;
  status: ConsumerStatus;
  address_line1: string;
  address_line2: string;
  city_town: string;
  district: string;
  state: string;
  pin_code: string;
  country: string;
  contact_person_name: string;
  designation: string;
  mobile_number: string;
  email_address: string;
  alternate_mobile: string;
  alternate_email: string;
};

type ConsumerUnit = {
  code: string;
  name: string;
  generating_company_code: string;
  generating_company_name: string;
  utility_id: number;
  pss_code: string;
  pss_name: string;
  installed_capacity_mw: number;
};

type ConsumerMappingRow = {
  id: number;
  consumer_id: number;
  utility_id: number;
  generating_company_code: string;
  generating_company_name: string;
  generating_unit_code: string;
  generating_unit_name: string;
  pss_code: string;
  pss_name: string;
  consumer_type: ConsumerType;
  contracted_capacity_mw: number;
  effective_from: string;
  effective_to: string;
  status: ConsumerStatus;
};

type ManageConsumerMock = {
  header: {
    title: string;
    description: string;
  };
  consumers: ConsumerProfile[];
  utilities: ConsumerUtility[];
  generatingUnits: ConsumerUnit[];
  consumerMappings: ConsumerMappingRow[];
};

type ConsumerTableRow = ConsumerProfile & {
  utility_name: string;
  mapped_units: number;
  mapped_pss: number;
  mapped_companies: number;
};

type ConsumerStat = {
  label: string;
  value: string;
  icon: string;
  tone: 'blue' | 'green' | 'violet' | 'amber' | 'red';
};

const DEFAULT_DATA: ManageConsumerMock = {
  header: {
    title: 'Manage Consumer',
    description: 'Map consumers with utility-linked generating units and review existing allocations.'
  },
  consumers: [],
  utilities: [],
  generatingUnits: [],
  consumerMappings: []
};

@Component({
  selector: 'app-manage-consumer',
  standalone: true,
  imports: [RouterLink],
  templateUrl: './manage-consumer.component.html',
  styleUrls: ['./manage-consumer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ManageConsumerComponent {
  private readonly http = inject(HttpClient);

  readonly pageData = signal<ManageConsumerMock>(DEFAULT_DATA);
  readonly searchTerm = signal('');
  readonly selectedConsumerType = signal<'All' | ConsumerType>('All');
  readonly selectedStatus = signal<'All' | ConsumerStatus>('All');
  readonly currentPage = signal(1);
  readonly pageSize = signal(8);

  readonly isMapUnitsModalOpen = signal(false);
  readonly activeTab = signal<'available' | 'mapped'>('available');
  readonly selectedConsumerId = signal<number | null>(null);
  readonly availableUnitSearch = signal('');
  readonly mappedUnitSearch = signal('');
  readonly selectedAvailableUnitCodes = signal<string[]>([]);
  readonly selectedMappedUnitCodes = signal<string[]>([]);

  readonly title = computed(() => this.pageData().header.title);
  readonly description = computed(() => this.pageData().header.description);
  readonly consumers = computed(() => this.pageData().consumers);
  readonly utilities = computed(() => this.pageData().utilities);
  readonly generatingUnits = computed(() => this.pageData().generatingUnits);
  readonly consumerMappings = computed(() => this.pageData().consumerMappings);
  readonly stats = computed<ConsumerStat[]>(() => {
    const consumers = this.consumers();
    const mappings = this.consumerMappings();
    const mappedUnitCodes = new Set(mappings.map((mapping) => mapping.generating_unit_code));
    const totalContractedCapacity = mappings.reduce((total, mapping) => total + mapping.contracted_capacity_mw, 0);

    return [
      { label: 'Total Consumers', value: String(consumers.length), icon: '◫', tone: 'blue' },
      { label: 'Active Consumers', value: String(consumers.filter((consumer) => consumer.status === 'Active').length), icon: '◎', tone: 'green' },
      { label: 'Mapped Units', value: String(mappedUnitCodes.size), icon: '▦', tone: 'violet' },
      { label: 'Available Units', value: String(this.generatingUnits().filter((unit) => !mappedUnitCodes.has(unit.code)).length), icon: '◌', tone: 'amber' },
      { label: 'Contracted Capacity', value: this.formatCapacity(totalContractedCapacity), icon: 'ϟ', tone: 'blue' }
    ];
  });

  readonly consumerRows = computed<ConsumerTableRow[]>(() => {
    const utilityMap = new Map(this.utilities().map((utility) => [utility.utility_id, utility.name]));
    return this.consumers().map((consumer) => {
      const mappings = this.consumerMappings().filter((mapping) => mapping.consumer_id === consumer.consumer_id);
      return {
        ...consumer,
        utility_name: utilityMap.get(consumer.utility_id) || 'Unknown Utility',
        mapped_units: mappings.length,
        mapped_pss: new Set(mappings.map((mapping) => mapping.pss_code)).size,
        mapped_companies: new Set(mappings.map((mapping) => mapping.generating_company_code)).size
      };
    });
  });

  readonly filteredRows = computed(() => {
    const search = this.searchTerm().trim().toLowerCase();
    const consumerType = this.selectedConsumerType();
    const status = this.selectedStatus();

    return this.consumerRows().filter((row) => {
      const matchesSearch = !search || [
        row.name,
        row.code,
        row.utility_name,
        row.contact_person_name,
        row.email_address
      ].some((value) => value.toLowerCase().includes(search));
      const matchesType = consumerType === 'All' || row.consumer_type === consumerType;
      const matchesStatus = status === 'All' || row.status === status;
      return matchesSearch && matchesType && matchesStatus;
    });
  });

  readonly totalPages = computed(() => Math.max(1, Math.ceil(this.filteredRows().length / this.pageSize())));
  readonly paginatedRows = computed(() => {
    const page = Math.min(this.currentPage(), this.totalPages());
    const start = (page - 1) * this.pageSize();
    return this.filteredRows().slice(start, start + this.pageSize());
  });
  readonly pageNumbers = computed(() => Array.from({ length: this.totalPages() }, (_, index) => index + 1));
  readonly showingText = computed(() => {
    const total = this.filteredRows().length;
    if (total === 0) {
      return 'Showing 0 entries';
    }
    const start = ((this.currentPage() - 1) * this.pageSize()) + 1;
    const end = Math.min(this.currentPage() * this.pageSize(), total);
    return `Showing ${start} to ${end} of ${total} entries`;
  });

  readonly selectedConsumer = computed(() => this.consumerRows().find((row) => row.consumer_id === this.selectedConsumerId()) || null);

  readonly mappedUnits = computed(() => {
    const consumerId = this.selectedConsumerId();
    if (!consumerId) {
      return [] as ConsumerMappingRow[];
    }
    return this.consumerMappings().filter((mapping) => mapping.consumer_id === consumerId);
  });

  readonly availableUnits = computed(() => {
    const consumer = this.selectedConsumer();
    if (!consumer) {
      return [] as ConsumerUnit[];
    }

    const mappedUnitCodes = new Set(this.mappedUnits().map((mapping) => mapping.generating_unit_code));
    return this.generatingUnits().filter((unit) => unit.utility_id === consumer.utility_id && !mappedUnitCodes.has(unit.code));
  });

  readonly filteredAvailableUnits = computed(() => {
    const search = this.availableUnitSearch().trim().toLowerCase();
    return this.availableUnits().filter((unit) =>
      !search || [
        unit.name,
        unit.generating_company_name,
        unit.pss_name,
        unit.code
      ].some((value) => value.toLowerCase().includes(search))
    );
  });

  readonly filteredMappedUnits = computed(() => {
    const search = this.mappedUnitSearch().trim().toLowerCase();
    return this.mappedUnits().filter((unit) =>
      !search || [
        unit.generating_unit_name,
        unit.generating_company_name,
        unit.pss_name,
        unit.generating_unit_code
      ].some((value) => value.toLowerCase().includes(search))
    );
  });

  constructor() {
    this.http.get<ManageConsumerMock>('json/manage-consumer.mock.json').subscribe({
      next: (data) => this.pageData.set(data),
      error: () => this.pageData.set(DEFAULT_DATA)
    });
  }

  updateSearch(value: string): void {
    this.searchTerm.set(value);
    this.currentPage.set(1);
  }

  updateConsumerType(value: 'All' | ConsumerType): void {
    this.selectedConsumerType.set(value);
    this.currentPage.set(1);
  }

  updateStatus(value: 'All' | ConsumerStatus): void {
    this.selectedStatus.set(value);
    this.currentPage.set(1);
  }

  resetFilters(): void {
    this.searchTerm.set('');
    this.selectedConsumerType.set('All');
    this.selectedStatus.set('All');
    this.currentPage.set(1);
  }

  goToPage(page: number): void {
    this.currentPage.set(Math.min(Math.max(page, 1), this.totalPages()));
  }

  openMapUnitsModal(consumerId: number): void {
    this.selectedConsumerId.set(consumerId);
    this.activeTab.set('available');
    this.availableUnitSearch.set('');
    this.mappedUnitSearch.set('');
    this.selectedAvailableUnitCodes.set([]);
    this.selectedMappedUnitCodes.set([]);
    this.isMapUnitsModalOpen.set(true);
  }

  closeMapUnitsModal(): void {
    this.isMapUnitsModalOpen.set(false);
    this.selectedConsumerId.set(null);
    this.selectedAvailableUnitCodes.set([]);
    this.selectedMappedUnitCodes.set([]);
  }

  switchTab(tab: 'available' | 'mapped'): void {
    this.activeTab.set(tab);
    this.selectedAvailableUnitCodes.set([]);
    this.selectedMappedUnitCodes.set([]);
  }

  updateAvailableUnitSearch(value: string): void {
    this.availableUnitSearch.set(value);
  }

  updateMappedUnitSearch(value: string): void {
    this.mappedUnitSearch.set(value);
  }

  toggleAvailableSelection(unitCode: string, checked: boolean): void {
    this.selectedAvailableUnitCodes.update((codes) =>
      checked ? [...codes, unitCode] : codes.filter((code) => code !== unitCode)
    );
  }

  toggleMappedSelection(unitCode: string, checked: boolean): void {
    this.selectedMappedUnitCodes.update((codes) =>
      checked ? [...codes, unitCode] : codes.filter((code) => code !== unitCode)
    );
  }

  addSelectedUnits(): void {
    const consumer = this.selectedConsumer();
    if (!consumer || this.selectedAvailableUnitCodes().length === 0) {
      return;
    }

    const selectedUnits = this.generatingUnits().filter((unit) => this.selectedAvailableUnitCodes().includes(unit.code));
    const nextId = this.consumerMappings().reduce((maxId, row) => Math.max(maxId, row.id), 0);

    const additions = selectedUnits.map((unit, index) => ({
      id: nextId + index + 1,
      consumer_id: consumer.consumer_id,
      utility_id: consumer.utility_id,
      generating_company_code: unit.generating_company_code,
      generating_company_name: unit.generating_company_name,
      generating_unit_code: unit.code,
      generating_unit_name: unit.name,
      pss_code: unit.pss_code,
      pss_name: unit.pss_name,
      consumer_type: consumer.consumer_type,
      contracted_capacity_mw: unit.installed_capacity_mw,
      effective_from: '2026-08-01',
      effective_to: '2027-03-31',
      status: 'Active' as ConsumerStatus
    }));

    this.pageData.update((data) => ({
      ...data,
      consumerMappings: [...data.consumerMappings, ...additions]
    }));

    this.selectedAvailableUnitCodes.set([]);
    this.activeTab.set('mapped');
  }

  removeSelectedUnits(): void {
    const selectedCodes = new Set(this.selectedMappedUnitCodes());
    if (selectedCodes.size === 0) {
      return;
    }

    this.pageData.update((data) => ({
      ...data,
      consumerMappings: data.consumerMappings.filter((mapping) => !selectedCodes.has(mapping.generating_unit_code))
    }));

    this.selectedMappedUnitCodes.set([]);
  }

  removeMappedUnit(unitCode: string): void {
    this.pageData.update((data) => ({
      ...data,
      consumerMappings: data.consumerMappings.filter((mapping) => !(mapping.consumer_id === this.selectedConsumerId() && mapping.generating_unit_code === unitCode))
    }));
  }

  formatDate(value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return new Intl.DateTimeFormat('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).format(date);
  }

  formatCapacity(value: number): string {
    return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(value);
  }
}
